// ---- The item card's pane switcher ----
//
// A compact pill segmented control (the admin app's SegToggle shape), deliberately NOT the underline
// tabs the action-point panel uses one level up — the two levels of navigation must never read as
// siblings. Rendered only when the card has more than one pane to offer, so a stripped-down card
// (e.g. the admin draft preview with documents and messages off) keeps its plain single-column body.
//
// The strip itself is the shared TabStrip: tablist semantics, roving tabindex, and the KEY-462 scroll
// buttons that replace a scrollbar once four tabs stop fitting an item card on a phone.

import type { IconName } from "../components/Icon";
import { TabStrip, type TabStripItem } from "../components/TabStrip";
import { useTabStripDisplay } from "../context";

export type ItemTabId = "progress" | "details" | "guidance" | "files" | "discussion";

/** What each pane is drawn as when the strips show icons — by what the pane does, not what it's called. */
const ITEM_TAB_ICON: Record<ItemTabId, IconName> = {
  progress: "target",
  details: "info",
  guidance: "compass",
  files: "clip",
  discussion: "chat",
};

export interface ItemTabDef {
  id: ItemTabId;
  label: string;
  /** Tally shown in the tab's badge; 0 renders no badge (an empty pane is still reachable). */
  count?: number;
  /** Amber attention dot — a parked status pick is waiting behind this pane. */
  alert?: boolean;
}

export function ItemTabs({ tabs, active, onSelect, groupLabel }: {
  tabs: readonly ItemTabDef[];
  active: ItemTabId;
  onSelect: (id: ItemTabId) => void;
  /** Accessible name for the tab group, e.g. the item's name. */
  groupLabel: string;
}) {
  // Follows the reader's labels/icons choice, but offers no toggle of its own: the action point's strip
  // just above it already does, and one switch per card is plenty.
  const { display } = useTabStripDisplay(false);
  const items: TabStripItem<ItemTabId>[] = tabs.map((t) => ({ ...t, icon: ITEM_TAB_ICON[t.id] }));
  return (
    <TabStrip<ItemTabId>
      items={items}
      display={display}
      active={active}
      onSelect={onSelect}
      variant="pill"
      size="sm"
      ariaLabel={groupLabel}
      // Full width of the card (KEY-485): packed to the left it read as a stray control sitting on the
      // card body rather than as the card's own navigation.
      fill
      style={{ alignSelf: "stretch" }}
    />
  );
}
